/**
 * Notes d'appel accrochées au numéro et « passages attendus » (demande William du 13/10/2026,
 * docs/specs/2026-10-13-note-appel-numero-design.md) : les appels au serveur, et la mise en forme
 * partagée par les écrans (décroché, fiche d'appel, liste, nouveau ticket, saisie quotidienne).
 *
 * Le serveur décide de tout ce qui se lit : `fiche: null` veut dire « rien pour toi », qu'il n'y
 * ait pas de fiche ou qu'elle ne soit pas lisible. Aucun écran ne filtre à sa place.
 */
import { apiFetch, readApiJson } from './api';

/** Durées de relance proposées, en jours ouvrés (les mêmes que le serveur accepte). */
export const JOURS_RELANCE = [1, 2, 3, 7];
export const JOURS_RELANCE_DEFAUT = 2;

const c = encodeURIComponent;
const enJson = (corps) => ({
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corps || {}),
});
const appeler = async (url, opts) => readApiJson(await apiFetch(url, opts));

export const lireFicheDuNumero = (numero) => appeler(`/api/numeros-fiches/par-numero?numero=${c(numero || '')}`);
export const lireFicheDeLAppel = (callId) => appeler(`/api/numeros-fiches/par-appel/${c(callId)}`);
export const lireFicheDuPassage = (passageId) => appeler(`/api/numeros-fiches/passages/${c(passageId)}`);
export const lirePassages = (q = '') => appeler(`/api/numeros-fiches/passages?q=${c(q)}`);
/** La même recherche, avec le statut HTTP : un refus de droit (403) ne se lit ni comme une panne, ni comme « rien ». */
export const chercherPassages = async (q = '') => {
  const reponse = await apiFetch(`/api/numeros-fiches/passages?q=${c(q)}`);
  return { statut: reponse && reponse.status, corps: await readApiJson(reponse) };
};
export const lirePassagesARelancer = () => appeler('/api/team/daily-log/passages-a-relancer');
export const envoyerNote = (corps) => appeler('/api/numeros-fiches/notes', enJson(corps));
export const reporterPassage = (passageId, corps) => appeler(`/api/numeros-fiches/passages/${c(passageId)}/reporter`, enJson(corps));
export const abandonnerPassage = (passageId, corps) => appeler(`/api/numeros-fiches/passages/${c(passageId)}/abandonner`, enJson(corps));
export const rattacherContact = (ficheId, userId) => appeler(`/api/numeros-fiches/${c(ficheId)}/contact`, enJson({ user_id: userId }));

/** readApiJson ne lève pas sur un 4xx JSON : il rend le corps (`detail` d'un refus de droit, `error` d'un refus métier). */
export const messageRefus = (res, defaut) => {
  const message = res && (res.detail || res.error);
  return typeof message === 'string' && message ? message : defaut;
};

/** « 15/10 » pour un jour (« 2026-10-15 ») comme pour un instant (heure locale du poste). */
export function jourCourt(valeur) {
  const texte = String(valeur || '');
  const jour = /^(\d{4})-(\d{2})-(\d{2})$/.exec(texte);
  if (jour) return `${jour[3]}/${jour[2]}`;
  const instant = new Date(texte);
  if (!texte || Number.isNaN(instant.getTime())) return '';
  return instant.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

/** Qui est attendu : le contact rattaché, sinon le nom noté, sinon le numéro. */
export const quiEstAttendu = (fiche) => (fiche && (fiche.nom_contact || fiche.nom || fiche.numero)) || 'Numéro masqué';

/**
 * Description proposée pour le ticket d'une personne qui est venue : ce que les techniciens ont
 * NOTÉ, de la plus ancienne note à la plus récente. Ni note privée, ni nom d'auteur, ni résumé
 * automatique : la description d'un ticket est la « demande initiale » du rapport remis au client.
 * Seulement les notes postérieures à la fermeture du DERNIER passage fermé (`passages_fermes`, le
 * plus récent d'abord) : un passage déjà repris et les mémos d'avant n'en font pas partie (R12, 14/10/2026).
 */
export function descriptionDepuisFiche(fiche) {
  const dernierFerme = ((fiche && fiche.passages_fermes) || [])[0];
  const depuis = Date.parse((dernierFerme && dernierFerme.ferme_le) || '');
  return [...((fiche && fiche.notes) || [])].reverse()
    .filter((n) => Number.isNaN(depuis) || Date.parse(n.cree_le || '') > depuis)
    .filter((n) => !n.prive && n.texte)
    .map((n) => n.texte)
    .join('\n\n');
}

/**
 * Avant de reprendre un passage : la personne doit être un contact. Rend `{ userId, clientId }`,
 * ou null si le technicien renonce ou si le serveur refuse (le refus est affiché).
 * `demanderNom` = le `usePrompt()` du portail.
 */
export async function preparerReprise(fiche, { demanderNom, toast }) {
  if (fiche.user_id) return { userId: fiche.user_id, clientId: fiche.client_id || '' };
  const saisi = await demanderNom('Nom complet de la personne :', { defaultValue: fiche.nom || '' });
  const morceaux = String(saisi || '').trim().split(/\s+/).filter(Boolean);
  const nom = morceaux.join(' ');
  if (!nom) return null;
  try {
    const cree = await appeler('/api/tk/admin/users', enJson({
      first_name: morceaux[0] || '', last_name: morceaux.slice(1).join(' '), username: nom,
      phone: fiche.numero || '', role: 'utilisateur',
    }));
    const contact = cree && (cree.user || cree);
    const userId = contact && (contact.id || contact.user_id);
    if (!cree || cree.ok === false || !userId) {
      toast.error(messageRefus(cree, 'Création du contact impossible'));
      return null;
    }
    const lie = await rattacherContact(fiche.id, userId);
    if (!lie || !lie.ok) {
      toast.error(messageRefus(lie, 'Le contact est créé, mais il n’a pas pu être rattaché au passage'));
      return null;
    }
    return { userId, clientId: (lie.fiche && lie.fiche.client_id) || '' };
  } catch (e) {
    toast.error((e && e.message) || 'Création du contact impossible');
    return null;
  }
}

/** Adresse du formulaire de nouveau ticket prérempli pour ce passage. */
export function adresseNouveauTicket(fiche, pret) {
  const params = new URLSearchParams({ passage_id: fiche.passage.id, requester_id: pret.userId });
  if (pret.clientId) params.set('client_id', pret.clientId);
  return `/tickets/new?${params}`;
}
